import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  FormControl, InputLabel, Select, MenuItem, Chip,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Alert, TableFooter, TablePagination
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../../context/Auth0Context';
import AddIcon from '@mui/icons-material/Add';
import TimeIntervalInput from '../common/TimeIntervalInput';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkIcon from '@mui/icons-material/Work';

const DailyEntry = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();

  // Helper function to format decimal hours as "Xh Ym"
  const formatHoursDisplay = (decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '-';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);

    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [projects, setProjects] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [wpsEntries, setWpsEntries] = useState([]);
  const [actualHoursEntries, setActualHoursEntries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projectStages, setProjectStages] = useState([]);

  // Pagination state - separate for each table (like the Workplanning page)
  const [wpsPage, setWpsPage] = useState(0);
  const [wpsRowsPerPage, setWpsRowsPerPage] = useState(15);
  const [timePage, setTimePage] = useState(0);
  const [timeRowsPerPage, setTimeRowsPerPage] = useState(15);

  // Reset WPS page if data shrinks past the last page (after filtering)
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(wpsEntries.length / wpsRowsPerPage) - 1);
    if (wpsPage > maxPage) setWpsPage(maxPage);
  }, [wpsEntries.length, wpsRowsPerPage, wpsPage]);

  // Reset Time Entries page if data shrinks past the last page
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(actualHoursEntries.length / timeRowsPerPage) - 1);
    if (timePage > maxPage) setTimePage(maxPage);
  }, [actualHoursEntries.length, timeRowsPerPage, timePage]);

  // Manual time entry state
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntryData, setManualEntryData] = useState({
    project_id: '',
    hours_normal: '',
    hours_outside: '',
    notes: '',
    start_date: null,
    end_date: null,
    job_code_id: '',
    department_id: '',
    project_lead_id: '',
    deliverable: ''
  });
  const [savingEntry, setSavingEntry] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // State for job number filtering - support multiple selections
  const [jobNumberInput, setJobNumberInput] = useState('');
  const [filteredJobNumbers, setFilteredJobNumbers] = useState([]);
  const [allJobNumbers, setAllJobNumbers] = useState([]);
  const [selectedJobNumbers, setSelectedJobNumbers] = useState([]); // Array of selected job numbers

  // Auto-set user for normal users
  useEffect(() => {
    if (currentUser && !isAdmin()) {
      const userId = currentUser.employee_id || currentUser.id;
      setSelectedUser(userId.toString());
    }
  }, [currentUser, isAdmin]);

  // Handle URL parameters for pre-selecting user
  useEffect(() => {
    const userId = searchParams.get('user_id');
    if (userId) {
      setSelectedUser(userId);
    }
  }, [searchParams]);

  const fetchData = useCallback(async () => {
    try {
      // Fetch basic required data
      const [projectsRes, usersRes, departmentsRes, projectStagesRes] = await Promise.all([
        axios.get('/api/projects', { withCredentials: true }),
        axios.get('/api/users', { withCredentials: true }),
        axios.get('/api/departments', { withCredentials: true }),
        axios.get('/api/project-stages', { withCredentials: true })
      ]);

      setProjects(projectsRes.data);
      setUsers(usersRes.data);
      setDepartments(departmentsRes.data);
      setProjectStages(projectStagesRes.data);

      // If a user is selected, fetch their assigned projects, WPS entries, and actual hours entries
      if (selectedUser) {
        const [assignedProjectsRes, wpsRes, actualHoursRes] = await Promise.all([
          axios.get(`/api/project-users?user_id=${selectedUser}`, { withCredentials: true }),
          axios.get('/api/planned-hours', {
            params: {
              user_id: selectedUser,
              start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
              end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined
            },
            withCredentials: true
          }),
          axios.get('/api/actual-hours', {
            params: {
              user_id: selectedUser,
              start_date: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
              end_date: endDate ? format(endDate, 'yyyy-MM-dd') : undefined
            },
            withCredentials: true
          })
        ]);

        // Map the project assignments to include full project details
        const assignedProjectsWithDetails = assignedProjectsRes.data.map(assignment => {
          const project = projectsRes.data.find(p => p.id === assignment.project_id);
          return {
            ...assignment,
            ...project
          };
        });

        // Filter WPS entries by date range if dates are provided
        let filteredWpsEntries = wpsRes.data;
        if (startDate && endDate) {
          const startDateStr = startDate.toISOString().split('T')[0];
          const endDateStr = endDate.toISOString().split('T')[0];
          
          filteredWpsEntries = wpsRes.data.filter(entry => {
            const entryStartDate = new Date(entry.start_date).toISOString().split('T')[0];
            const entryEndDate = new Date(entry.end_date).toISOString().split('T')[0];
            
            // Include entry if there's any overlap between the filter range and entry range
            return entryStartDate <= endDateStr && entryEndDate >= startDateStr;
          });
        }

        // Filter actual hours entries by date range if dates are provided
        let filteredActualHours = actualHoursRes.data;
        if (startDate && endDate) {
          const startDateStr = startDate.toISOString().split('T')[0];
          const endDateStr = endDate.toISOString().split('T')[0];
          
          filteredActualHours = actualHoursRes.data.filter(entry => {
            const entryDate = new Date(entry.date).toISOString().split('T')[0];
            return entryDate >= startDateStr && entryDate <= endDateStr;
          });
        }

        setAssignedProjects(assignedProjectsWithDetails);
        setWpsEntries(filteredWpsEntries);
        setActualHoursEntries(filteredActualHours);
      } else {
        setAssignedProjects([]);
        setWpsEntries([]);
        setActualHoursEntries([]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  }, [selectedUser, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Populate all job numbers from assigned projects
  useEffect(() => {
    const jobNums = assignedProjects.map(p => p.job_number).filter(Boolean);
    setAllJobNumbers(jobNums);
  }, [assignedProjects]);

  // Filter job numbers as user types
  useEffect(() => {
    if (jobNumberInput.trim() === '') {
      setFilteredJobNumbers(allJobNumbers);
    } else {
      const filtered = allJobNumbers.filter(jobNum =>
        jobNum.toString().toLowerCase().includes(jobNumberInput.toLowerCase())
      );
      setFilteredJobNumbers(filtered);
    }
  }, [jobNumberInput, allJobNumbers]);

  const handleUserChange = (e) => {
    setSelectedUser(e.target.value);
    // Reset manual entry form when user changes
    setShowManualEntry(false);
    setManualEntryData({
      project_id: '',
      hours_normal: '',
      hours_outside: '',
      notes: '',
      start_date: null,
      end_date: null,
      job_code_id: '',
      department_id: ''
    });
    setSelectedJobNumbers([]);
  };

  // handleUpdateWPSEntry function removed - users can only update actual hours through WPS edit button

  // Manual time entry functions
  const handleManualEntryChange = (field, value) => {
    setManualEntryData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveManualEntry = async () => {
    try {
      setSavingEntry(true);

      // Validate required fields - check if job numbers are selected
      if (selectedJobNumbers.length === 0 && !manualEntryData.project_id) {
        alert('Please select at least one job number or project');
        return;
      }

      if (!manualEntryData.start_date || !manualEntryData.end_date) {
        alert('Please fill in all required fields:\n• Activity Start Date\n• Activity End Date');
        return;
      }

      // Validate at least one hours field is filled
      const hoursNormal = parseFloat(manualEntryData.hours_normal) || 0;
      const hoursOutside = parseFloat(manualEntryData.hours_outside) || 0;
      const totalHours = hoursNormal + hoursOutside;

      if (totalHours <= 0) {
        alert('Please enter hours worked:\n• At least one of "Hours Between 8H00-17H00" or "Hours Before 8H00 & After 17H00" must be greater than 0');
        return;
      }

      // Validate hours range - allow reasonable project hours over the activity period
      if (hoursNormal < 0 || hoursOutside < 0) {
        alert('Hours cannot be negative:\n• Please enter valid positive hours');
        return;
      }

      // Reasonable upper limit for project activities (e.g., 200 hours per activity period)
      if (hoursNormal > 200 || hoursOutside > 200 || totalHours > 400) {
        alert('Hours seem unusually high:\n• Normal hours: max 200\n• Outside hours: max 200\n• Total hours: max 400\n\nPlease verify your entries or split into multiple activities.');
        return;
      }

      // Get projects to create entries for
      const projectsToCreate = selectedJobNumbers.length > 0
        ? selectedJobNumbers.map(jobNum => assignedProjects.find(p => p.job_number === jobNum)).filter(Boolean)
        : [assignedProjects.find(p => p.id === parseInt(manualEntryData.project_id))].filter(Boolean);

      if (projectsToCreate.length === 0) {
        alert('No valid projects found for the selected job numbers');
        return;
      }

      console.log(`🔧 Creating ${projectsToCreate.length} time entries and WPS entries...`);

      const createdEntries = [];
      const createdWPS = [];
      const errors = [];

      // Create time entry and WPS for each project
      for (const project of projectsToCreate) {
        try {
          // Create the timesheet entry
          const entryData = {
            project_id: project.id,
            user_id: parseInt(selectedUser),
            hours: totalHours,
            hours_normal: hoursNormal,
            hours_outside: hoursOutside,
            date: format(manualEntryData.start_date, 'yyyy-MM-dd'),
            notes: manualEntryData.notes || '',
            start_date: format(manualEntryData.start_date, 'yyyy-MM-dd'),
            end_date: format(manualEntryData.end_date, 'yyyy-MM-dd'),
            job_code_id: manualEntryData.job_code_id ? parseInt(manualEntryData.job_code_id) : null,
            project_stage_id: manualEntryData.project_stage_id ? parseInt(manualEntryData.project_stage_id) : null,
            project_lead_id: manualEntryData.project_lead_id ? parseInt(manualEntryData.project_lead_id) : null,
            deliverable: manualEntryData.deliverable || '',
            from_wps: false
          };

          console.log(`🔧 Saving time entry for project ${project.name} (${project.job_number}):`, entryData);
          const timeEntryResponse = await axios.post('/api/actual-hours', entryData, { withCredentials: true });
          console.log(`✅ Time entry saved for ${project.name}:`, timeEntryResponse.data);
          createdEntries.push({ project, response: timeEntryResponse.data });

          // Create corresponding WPS entry
          const wpsData = {
            project_id: project.id,
            user_id: parseInt(selectedUser),
            task_id: null,
            hours: hoursNormal,
            hours_outside_normal: hoursOutside,
            week_start: format(manualEntryData.start_date, 'yyyy-MM-dd'),
            start_date: format(manualEntryData.start_date, 'yyyy-MM-dd'),
            end_date: format(manualEntryData.end_date, 'yyyy-MM-dd'),
            job_number: project.job_number,
            actual_duration: totalHours,
            comments: manualEntryData.notes || '',
            status: 'draft',
            job_code_id: manualEntryData.job_code_id ? parseInt(manualEntryData.job_code_id) : null,
            project_stage_id: project.project_stage_id || null,
            deliverable: manualEntryData.deliverable || ''
          };

          console.log(`🔧 Creating WPS entry for project ${project.name} (${project.job_number}):`, wpsData);
          const wpsResponse = await axios.post('/api/planned-hours', wpsData, { withCredentials: true });
          console.log(`✅ WPS entry created for ${project.name}:`, wpsResponse.data);
          createdWPS.push({ project, response: wpsResponse.data });

        } catch (err) {
          console.error(`Error creating entries for project ${project.name}:`, err);
          errors.push({ project, error: err.message });
        }
      }

      // Reset form and close
      setManualEntryData({
        project_id: '',
        hours_normal: '',
        hours_outside: '',
        notes: '',
        start_date: null,
        end_date: null,
        job_code_id: '',
        department_id: '',
        project_lead_id: '',
        deliverable: ''
      });
      setSelectedJobNumbers([]);
      setShowManualEntry(false);

      // Refresh data to show the new entries
      fetchData();

      // Show success message
      const successCount = createdEntries.length;
      const wpsCount = createdWPS.length;
      const errorCount = errors.length;

      let message = `Successfully created ${successCount} time ${successCount === 1 ? 'entry' : 'entries'} and ${wpsCount} WPS ${wpsCount === 1 ? 'entry' : 'entries'}!\n`;
      message += `Total Hours: ${totalHours}h | Period: ${format(manualEntryData.start_date, 'dd/MM/yyyy')} - ${format(manualEntryData.end_date, 'dd/MM/yyyy')}\n`;
      message += `Projects: ${createdEntries.map(e => e.project.name).join(', ')}`;

      if (errorCount > 0) {
        message += `\n\n⚠️ ${errorCount} ${errorCount === 1 ? 'error' : 'errors'} occurred: ${errors.map(e => e.project.name).join(', ')}`;
      }

      setSuccessMessage(message);

      // Clear success message after 8 seconds (longer for multiple entries)
      setTimeout(() => setSuccessMessage(''), 8000);
    } catch (err) {
      console.error('Error saving manual time entries:', err);
      alert('Failed to save time entries. Please try again.');
    } finally {
      setSavingEntry(false);
    }
  };

  const handleCancelManualEntry = () => {
    setManualEntryData({
      project_id: '',
      hours_normal: '',
      hours_outside: '',
      notes: '',
      start_date: null,
      end_date: null,
      job_code_id: '',
      department_id: ''
    });
    setSelectedJobNumbers([]);
    setShowManualEntry(false);
  };



  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Timesheet Entry
      </Typography>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Only show user dropdown for admin users */}
        {isAdmin() && (
          <FormControl sx={{ flex: 1, minWidth: '200px' }}>
            <InputLabel id="user-select-label">User</InputLabel>
            <Select
              labelId="user-select-label"
              value={selectedUser}
              onChange={handleUserChange}
              label="User"
            >
              <MenuItem value=""><em>Select a user</em></MenuItem>
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Start Date"
            value={startDate}
            format="dd/MM/yyyy"
            onChange={(newValue) => setStartDate(newValue)}
            slotProps={{
              textField: {
                variant: "outlined",
                sx: { minWidth: '150px' }
              }
            }}
          />
        </LocalizationProvider>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="End Date"
            value={endDate}
            format="dd/MM/yyyy"
            onChange={(newValue) => setEndDate(newValue)}
            slotProps={{
              textField: {
                variant: "outlined",
                sx: { minWidth: '150px' }
              }
            }}
          />
        </LocalizationProvider>

        {/* Manual Time Entry Button */}
        {selectedUser && assignedProjects.length > 0 && (
          <Tooltip title="Add a manual timesheet entry for work not covered by WPS">
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setShowManualEntry(true)}
              sx={{ minHeight: '56px' }}
            >
              ADD Manual Entry
            </Button>
          </Tooltip>
        )}
      </Box>

      {selectedUser ? (
        <>
          {/* Manual Time Entry Dialog - Enhanced Design */}
          <Dialog 
            open={showManualEntry} 
            onClose={handleCancelManualEntry}
            maxWidth="lg"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                minHeight: '80vh'
              }
            }}
          >
            <DialogTitle sx={{ 
              bgcolor: 'primary.main', 
              color: 'white', 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              py: 2
            }}>
              <AddIcon />
              <Typography variant="h5" fontWeight="600">
                Add Manual Time Entry
              </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, color: 'text.primary', fontWeight: 'medium' }}>
                Time Entry Details
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Row 1: Project Name and Job Number */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth required variant="outlined">
                      <InputLabel id="project-label">Project Name</InputLabel>
                      <Select
                        labelId="project-label"
                        value={manualEntryData.project_id}
                        onChange={(e) => handleManualEntryChange('project_id', e.target.value)}
                        label="Project Name"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select a project</em>
                        </MenuItem>
                        {assignedProjects.length > 0 ? (
                          assignedProjects.map((project) => (
                            <MenuItem key={project.id} value={project.id}>
                              {project.name} {project.job_number && `(${project.job_number})`}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            <em>No projects assigned - Contact admin</em>
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: 1, position: 'relative' }}>
                    <TextField
                      fullWidth
                      label="Job Number"
                      value={jobNumberInput}
                      onChange={(e) => {
                        const value = e.target.value;
                        setJobNumberInput(value);
                        // Clear selected job number when user starts typing again
                        if (selectedJobNumbers.length > 0) {
                          setSelectedJobNumbers([]);
                        }
                      }}
                      placeholder="Type to search job number"
                      variant="outlined"
                    />
                    {jobNumberInput && filteredJobNumbers.length > 0 && selectedJobNumbers.length === 0 && (
                      <Paper sx={{ position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: 200, overflow: 'auto', zIndex: 1000, mt: 1 }}>
                        {filteredJobNumbers.map((jobNum) => (
                          <Box
                            key={jobNum}
                            onClick={() => {
                              // Set single job number selection
                              setSelectedJobNumbers([jobNum]);
                              // Set the input to show the selected value
                              setJobNumberInput(jobNum);
                              // Find and set the corresponding project
                              const matchingProject = assignedProjects.find(p => p.job_number === jobNum);
                              if (matchingProject) {
                                handleManualEntryChange('project_id', matchingProject.id);
                              }
                            }}
                            sx={{
                              p: 1.5,
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: '#f5f5f5' },
                              borderBottom: '1px solid #eee'
                            }}
                          >
                            {jobNum}
                          </Box>
                        ))}
                      </Paper>
                    )}
                    {selectedJobNumbers.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={selectedJobNumbers[0]}
                          onDelete={() => {
                            setSelectedJobNumbers([]);
                            setJobNumberInput('');
                          }}
                          size="small"
                          color="primary"
                        />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Row 2: Project Stage and Department */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="project-stage-label">Project Stage</InputLabel>
                      <Select
                        labelId="project-stage-label"
                        value={manualEntryData.project_stage_id || ''}
                        onChange={(e) => handleManualEntryChange('project_stage_id', e.target.value)}
                        label="Project Stage"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>No project stage</em>
                        </MenuItem>
                        {projectStages.map((stage) => (
                          <MenuItem key={stage.id} value={stage.id}>
                            {stage.stage_number > 6 ? stage.name : `${stage.stage_number}. ${stage.name}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="department-label">Department</InputLabel>
                      <Select
                        labelId="department-label"
                        value={manualEntryData.department_id}
                        onChange={(e) => handleManualEntryChange('department_id', e.target.value)}
                        label="Department"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>No department</em>
                        </MenuItem>
                        {departments.map((dept) => (
                          <MenuItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Row 3: Project Lead and Deliverable */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="project-lead-label">Project Lead</InputLabel>
                      <Select
                        labelId="project-lead-label"
                        value={manualEntryData.project_lead_id || ''}
                        onChange={(e) => setManualEntryData(prev => ({
                          ...prev,
                          project_lead_id: e.target.value
                        }))}
                        label="Project Lead"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="">
                          <em>Select project lead</em>
                        </MenuItem>
                        {users.filter(user => user.project_lead === 1).map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="Deliverable"
                      value={manualEntryData.deliverable || ''}
                      onChange={(e) => setManualEntryData(prev => ({
                        ...prev,
                        deliverable: e.target.value
                      }))}
                      placeholder="Enter deliverable"
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Box>
                </Box>

                {/* Row 4: User and Hours Between 8H00-17H00 */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label="User"
                      value={users.find(u => u.id === parseInt(selectedUser))?.name || 'Unknown User'}
                      disabled
                      variant="outlined"
                      sx={{ bgcolor: 'grey.50' }}
                      helperText="Current logged-in user"
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <TimeIntervalInput
                      label="HOURS BETWEEN 8H00 and 17H00"
                      value={parseFloat(manualEntryData.hours_normal) || 0}
                      onChange={(value) => handleManualEntryChange('hours_normal', value)}
                      helperText="Total hours worked during normal business hours for this activity period"
                    />
                  </Box>
                </Box>

                {/* Row 5: Hours Before 8H00 & After 17H00 */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <TimeIntervalInput
                      label="HOURS BEFORE 8H00 & AFTER 17H00"
                      value={parseFloat(manualEntryData.hours_outside) || 0}
                      onChange={(value) => handleManualEntryChange('hours_outside', value)}
                      helperText="Total hours worked outside normal business hours for this activity period"
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    {/* Empty space for visual balance */}
                  </Box>
                </Box>

                {/* Row 6: Activity Dates */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Activity Start Date *"
                        value={manualEntryData.start_date}
                        onChange={(newValue) => handleManualEntryChange('start_date', newValue)}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            variant: 'outlined',
                            sx: { bgcolor: 'white' }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Activity End Date *"
                        value={manualEntryData.end_date}
                        onChange={(newValue) => handleManualEntryChange('end_date', newValue)}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            variant: 'outlined',
                            sx: { bgcolor: 'white' }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>

                {/* Row 7: Comments */}
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Comments"
                    value={manualEntryData.notes}
                    onChange={(e) => handleManualEntryChange('notes', e.target.value)}
                    placeholder="Describe the work performed, any issues encountered, or additional notes..."
                    variant="outlined"
                    sx={{ bgcolor: 'white' }}
                  />
                </Box>
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ 
              p: 3, 
              gap: 2, 
              bgcolor: 'grey.50',
              borderTop: '1px solid',
              borderTopColor: 'divider'
            }}>
              <Button
                onClick={handleCancelManualEntry}
                startIcon={<CancelIcon />}
                variant="outlined"
                size="large"
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveManualEntry}
                startIcon={<SaveIcon />}
                variant="contained"
                size="large"
                disabled={savingEntry}
                sx={{ minWidth: 120 }}
              >
                {savingEntry ? 'Saving...' : 'Save Entry'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* WPS Entries Section - Enhanced with All Fields */}
          {wpsEntries.length > 0 ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkIcon />
                Work Planning Schedule (WPS) Entries
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {startDate && endDate 
                  ? `Showing WPS entries from ${format(startDate, 'dd/MM/yyyy')} to ${format(endDate, 'dd/MM/yyyy')}`
                  : 'Showing all WPS entries for this user'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Update the actual hours you worked on these planned activities. This will automatically create timesheet entries.
              </Typography>
              <Box sx={{
                width: '100%',
                maxWidth: 'calc(100vw - 400px)', // Bring it in just a little
                overflow: 'hidden',
                boxSizing: 'border-box',
                mx: 'auto' // Center the container
              }}>
                <Paper sx={{
                  mb: 2,
                  borderRadius: '16px',
                  boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)'
                }}>
                  <TableContainer
                    sx={{
                      overflowX: 'auto',
                      width: '100%',
                      '&::-webkit-scrollbar': {
                        height: 8,
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: 4,
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: 4,
                        '&:hover': {
                          backgroundColor: '#a8a8a8',
                        }
                      }
                    }}
                  >
                  <Table size="small" sx={{ minWidth: 1200 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }} align="center">Job Number</TableCell>
                      <TableCell sx={{ minWidth: 300, fontWeight: 'bold' }} align="center">Project Name</TableCell>
                      <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }} align="center">Deliverables</TableCell>
                      <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }} align="center">User</TableCell>
                      <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }} align="center">Normal Hours</TableCell>
                      <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }} align="center">Outside Hours</TableCell>
                      <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }} align="center">Actual Normal</TableCell>
                      <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }} align="center">Actual Outside</TableCell>
                      <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }} align="center">Start Date</TableCell>
                      <TableCell sx={{ minWidth: 100, fontWeight: 'bold' }} align="center">End Date</TableCell>
                      <TableCell sx={{ minWidth: 120, fontWeight: 'bold' }} align="center">Department</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {wpsEntries
                    .slice(wpsPage * wpsRowsPerPage, wpsPage * wpsRowsPerPage + wpsRowsPerPage)
                    .map((wpsEntry, index) => {
                      const project = projects.find((p) => p.id === wpsEntry.project_id);

                      return (
                        <TableRow key={wpsEntry.id} hover sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                          <TableCell sx={{ py: 1.5 }} align="center">
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {project?.job_number || wpsEntry.job_number || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5, whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 400 }}>
                            <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.4 }}>
                              {wpsEntry.project_name || 'Unknown Project'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {wpsEntry.deliverable || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {wpsEntry.user_name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }} align="center">
                            <Typography variant="body2" fontWeight="medium" sx={{ whiteSpace: 'nowrap' }}>
                              {formatHoursDisplay(wpsEntry.hours)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }} align="center">
                            <Typography variant="body2" fontWeight="medium" sx={{ whiteSpace: 'nowrap' }}>
                              {formatHoursDisplay(wpsEntry.hours_outside_normal)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }} align="center">
                            <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                              {formatHoursDisplay(wpsEntry.actual_hours_normal)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }} align="center">
                            <Typography variant="body2" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                              {formatHoursDisplay(wpsEntry.actual_hours_outside_normal)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {wpsEntry.start_date ? format(new Date(wpsEntry.start_date), 'dd/MM/yyyy') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {wpsEntry.end_date ? format(new Date(wpsEntry.end_date), 'dd/MM/yyyy') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                              {wpsEntry.department_name || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                  </Table>
                </TableContainer>
                {/* Pagination sits below the scrollable table so it's always visible */}
                <TablePagination
                  component="div"
                  count={wpsEntries.length}
                  rowsPerPage={wpsRowsPerPage}
                  page={wpsPage}
                  onPageChange={(event, newPage) => setWpsPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setWpsRowsPerPage(parseInt(event.target.value, 10));
                    setWpsPage(0);
                  }}
                  rowsPerPageOptions={[15, 30, 50]}
                  labelRowsPerPage="Rows per page:"
                />
                </Paper>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                No WPS Entries Found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {startDate && endDate 
                  ? `You don't have any Work Planning Schedule entries between ${format(startDate, 'dd/MM/yyyy')} and ${format(endDate, 'dd/MM/yyyy')}.`
                  : 'You don\'t have any Work Planning Schedule entries.'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {startDate && endDate 
                  ? 'Try selecting a different date range above, create WPS entries in the Work Planning page first, or use the ADD Manual Entry button above.'
                  : 'Try selecting a date range above to filter entries, create WPS entries in the Work Planning page first, or use the ADD Manual Entry button above.'
                }
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Actual Hours (Time Entries) Section - NEW */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon />
              Time Entries (Actual Hours)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {startDate && endDate 
                ? `Showing time entries from ${format(startDate, 'dd/MM/yyyy')} to ${format(endDate, 'dd/MM/yyyy')}`
                : 'Showing all time entries for this user'
              }
            </Typography>
            
            {actualHoursEntries.length > 0 ? (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">Job Number</TableCell>
                      <TableCell align="center">Project Name</TableCell>
                      <TableCell align="center">Deliverables</TableCell>
                      <TableCell align="center">Normal Hours</TableCell>
                      <TableCell align="center">Outside Hours</TableCell>
                      <TableCell align="center">Total Hours</TableCell>
                      <TableCell align="center">Source</TableCell>
                      <TableCell align="center">Start Date</TableCell>
                      <TableCell align="center">End Date</TableCell>
                      <TableCell align="center">Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {actualHoursEntries
                    .slice(timePage * timeRowsPerPage, timePage * timeRowsPerPage + timeRowsPerPage)
                    .map((entry) => {
                      const project = projects.find((p) => p.id === entry.project_id);
                      return (
                      <TableRow
                        key={entry.id}
                        hover
                        sx={{
                          backgroundColor: entry.from_wps ? 'rgba(25, 118, 210, 0.04)' : 'inherit',
                          '&:hover': {
                            backgroundColor: entry.from_wps ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <TableCell align="center">
                          <Typography variant="body2">
                            {project?.job_number || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 400 }}>
                          <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.4 }}>
                            {entry.project_name || 'Unknown Project'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.deliverable || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">
                            {Number(entry.hours_normal || 0).toFixed(2)}h
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">
                            {Number(entry.hours_outside || 0).toFixed(2)}h
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {Number(entry.hours || 0).toFixed(2)}h
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={entry.from_wps ? 'From WPS' : 'Manual'}
                            size="small"
                            color={entry.from_wps ? 'primary' : 'default'}
                            variant={entry.from_wps ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.start_date ? format(new Date(entry.start_date), 'dd/MM/yyyy') : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.end_date ? format(new Date(entry.end_date), 'dd/MM/yyyy') : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: '200px' }}>
                            {entry.notes || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                  <TableFooter>
                    <TablePagination
                      count={actualHoursEntries.length}
                      rowsPerPage={timeRowsPerPage}
                      page={timePage}
                      onPageChange={(event, newPage) => setTimePage(newPage)}
                      onRowsPerPageChange={(event) => {
                        setTimeRowsPerPage(parseInt(event.target.value, 10));
                        setTimePage(0);
                      }}
                      rowsPerPageOptions={[15, 30, 50]}
                      labelRowsPerPage="Rows per page:"
                    />
                  </TableFooter>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>
                  No Time Entries Found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {startDate && endDate 
                    ? `No time entries found between ${format(startDate, 'dd/MM/yyyy')} and ${format(endDate, 'dd/MM/yyyy')}.`
                    : 'No time entries found for this user.'
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Time entries are created when you update WPS with actual hours or add manual entries using the button above.
                </Typography>
              </Box>
            )}
          </Box>

        </>
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            {isAdmin()
              ? "Please select a User to view and manage timesheets"
              : "Loading your timesheet data..."
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isAdmin()
              ? "Timesheets are tracked per user and project"
              : "Please wait while we load your assigned projects and timesheet entries"
            }
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DailyEntry;